import type { FC } from 'react';
import React from 'react';
import cn from 'classnames';
import { FontStyrene } from '@alfa-bank/site-font-styrene';

import { getScoreColorClassName } from '../../helpers';

import {
    PENTAGRAM_MAX_NORMAL_SCORE,
    PENTAGRAM_MIN_NORMAL_SCORE,
    PENTAGRAM_SCORE_COMMON_OFFSET_X,
    PENTAGRAM_SCORE_DESCRIPTION_OFFSET_X,
    PENTAGRAM_SCORE_DESCRIPTION_OFFSET_Y,
    PENTAGRAM_SCORE_EXTRA_OFFSET_X,
} from './constants';
import type { IPentagramScores } from './models';
import {
    getPentagramPointCoordinateX,
    getPentagramPointCoordinateY,
    getScoreStringValue,
} from './helpers';
import styles from './styles.css';

export const Scores: FC<IPentagramScores> = ({ angle, radius, scores }) => {
    return (
        <>
            {scores.map((score, index) => {
                const scoreCoordinateX = getPentagramPointCoordinateX(
                    radius +
                        ([1, 4].includes(index)
                            ? PENTAGRAM_SCORE_EXTRA_OFFSET_X
                            : PENTAGRAM_SCORE_COMMON_OFFSET_X),
                    index,
                    angle
                );
                const scoreClassName = getScoreColorClassName(
                    score.value,
                    PENTAGRAM_MIN_NORMAL_SCORE,
                    PENTAGRAM_MAX_NORMAL_SCORE
                );

                const scoreDescriptions = score.typeDescription.split(' ');

                return (
                    <text
                        key={score.typeDescription}
                        className={cn(styles.score, {
                            [styles.textAnchorStart]: [1, 2].includes(index),
                            [styles.textAnchorEnd]: [3, 4].includes(index),
                            [styles.textAnchorMiddle]: index === 0,
                        })}
                        x={getPentagramPointCoordinateX(
                            radius + PENTAGRAM_SCORE_DESCRIPTION_OFFSET_X,
                            index,
                            angle
                        )}
                        y={getPentagramPointCoordinateY(
                            radius + PENTAGRAM_SCORE_DESCRIPTION_OFFSET_Y,
                            index,
                            angle
                        )}
                    >
                        {index === 0 ? (
                            <>
                                <tspan
                                    className={styles.scoreDescription}
                                    dy="-5"
                                    x={scoreCoordinateX}
                                >
                                    {score.typeDescription}
                                </tspan>
                                <FontStyrene>
                                    <tspan
                                        className={cn(styles.scoreValue, styles[scoreClassName])}
                                        dy="20"
                                        x={scoreCoordinateX}
                                    >
                                        {getScoreStringValue(score.value)}
                                    </tspan>
                                </FontStyrene>
                            </>
                        ) : (
                            <>
                                <FontStyrene>
                                    <tspan
                                        className={cn(styles.scoreValue, styles[scoreClassName])}
                                        dy={[2, 3].includes(index) ? 5 : 0}
                                        x={scoreCoordinateX}
                                    >
                                        {getScoreStringValue(score.value)}
                                    </tspan>
                                </FontStyrene>
                                {scoreDescriptions.map(descriptionLine => (
                                    <tspan
                                        key={descriptionLine}
                                        className={styles.scoreDescription}
                                        dy="18"
                                        x={scoreCoordinateX}
                                    >
                                        {descriptionLine}
                                    </tspan>
                                ))}
                            </>
                        )}
                    </text>
                );
            })}
        </>
    );
};
